import type { MethodOrInstrument } from '../../../../PaymentMethodSelector/types';
import type { CartSubmitErrorScenario } from '../mapCartSubmitError';
import { type CheckoutPaymentFailureTracking, type CheckoutScenario, formatMinorUnits } from '../types';

type CheckoutDialogTracking = {
  dataTrackType: string;
  dataTrackMessage: string;
};

const CHECKOUT_DIALOG_TRACKING = {
  depositLimitAmountExceeded: { dataTrackType: 'Deposit Limit', dataTrackMessage: 'Amount Exceeded' },
  depositLimitReached: { dataTrackType: 'Deposit Limit', dataTrackMessage: 'Limit Reached' },
  incorrectPaymentDetails: { dataTrackType: 'Incorrect Payment Details', dataTrackMessage: 'Check your payment details' },
  paymentFailedServersDown: { dataTrackType: 'Payment Failed', dataTrackMessage: 'Servers Down' },
  paymentFailedProviderRejected: { dataTrackType: 'Payment Failed', dataTrackMessage: 'Provider Rejected' },
  accountOnHoldVerifyIdentity: { dataTrackType: 'Account On Hold', dataTrackMessage: 'Verify Identity' },
  genericError: { dataTrackType: 'Generic Error', dataTrackMessage: 'Something Went Wrong' },
} as const satisfies Record<string, CheckoutDialogTracking>;

type CheckoutPaymentErrorContext = {
  httpStatus?: number;
  transactionState?: string;
  errorKey?: string;
};

function mapCheckoutPaymentErrorTracking(context: CheckoutPaymentErrorContext = {}): CheckoutDialogTracking {
  const normalisedErrorKey = context.errorKey?.toLowerCase() ?? '';

  if (normalisedErrorKey.includes('incorrect') || normalisedErrorKey.includes('invalid') || normalisedErrorKey.includes('validation')) {
    return CHECKOUT_DIALOG_TRACKING.incorrectPaymentDetails;
  }

  if (context.transactionState === 'REJECTED' || normalisedErrorKey.includes('reject') || normalisedErrorKey.includes('declined')) {
    return CHECKOUT_DIALOG_TRACKING.paymentFailedProviderRejected;
  }

  if (context.httpStatus !== undefined && context.httpStatus >= 500) {
    return CHECKOUT_DIALOG_TRACKING.paymentFailedServersDown;
  }

  return CHECKOUT_DIALOG_TRACKING.paymentFailedServersDown;
}

function mapCheckoutCartSubmitErrorTracking(scenario: CartSubmitErrorScenario): CheckoutDialogTracking | undefined {
  if (scenario === 'submit-failed') {
    return CHECKOUT_DIALOG_TRACKING.genericError;
  }

  return undefined;
}

function mapCheckoutCartSubmitErrorContainerTracking(scenario: CartSubmitErrorScenario): string | undefined {
  if (scenario === 'insufficient-balance') {
    return 'Balance Updated';
  }

  if (scenario === 'cart-adjusted') {
    return 'Bet Adjusted';
  }

  return undefined;
}

type DepositTrackingType = 'FTD' | 'SD';

function getDepositTrackingType(isFirstPayIn: boolean): DepositTrackingType {
  return isFirstPayIn ? 'FTD' : 'SD';
}

function buildCheckoutPaymentFailureTracking(method: MethodOrInstrument | null, amount: string, depositType?: DepositTrackingType): CheckoutPaymentFailureTracking {
  return {
    method: method?.['data-track-method'] ?? method?.name ?? '',
    provider: method?.['data-track-provider'] ?? method?.apiName ?? '',
    ...(depositType ? { type: depositType } : {}),
    count: '1',
    amount,
  };
}

const CHECKOUT_EMBEDDED_CASHIER_TRACKING = {
  category: 'No Balance',
  count: '1',
} as const;

const CHECKOUT_FULL_BALANCE_TRACKING = {
  category: 'Full Balance',
  method: 'Balance',
} as const;

const CHECKOUT_PARTIAL_BALANCE_TRACKING = {
  category: 'Partial Balance',
} as const;

const CHECKOUT_VOUCHER_TRACKING = {
  category: 'Voucher',
  method: 'Voucher',
} as const;

const CHECKOUT_PARTIAL_VOUCHER_TRACKING = {
  category: 'Partial Voucher',
  count: CHECKOUT_EMBEDDED_CASHIER_TRACKING.count,
} as const;

const CHECKOUT_MIXED_TRACKING = {
  category: 'Mixed',
  count: CHECKOUT_EMBEDDED_CASHIER_TRACKING.count,
} as const;

const CHECKOUT_DISCOUNT_TRACKING = {
  category: 'Discount',
  count: CHECKOUT_EMBEDDED_CASHIER_TRACKING.count,
} as const;

const CHECKOUT_BELOW_MINIMUM_PRE_STEP_THRESHOLD = 100;

const EMBEDDED_CASHIER_TRACKED_VARIANTS = ['no-balance', 'partial-balance', 'full-balance', 'full-voucher', 'partial-voucher', 'mixed', 'discount'] as const;

type EmbeddedCashierVariant = (typeof EMBEDDED_CASHIER_TRACKED_VARIANTS)[number];

type EmbeddedCashierContinueContext = {
  onPay: (method: MethodOrInstrument | null) => void;
  selectedMethod: MethodOrInstrument | null;
  onNoBalanceContinue: () => void;
};

function getEmbeddedCashierVariant(scenario: CheckoutScenario | undefined, payable: number): EmbeddedCashierVariant | undefined {
  if (!scenario) {
    return undefined;
  }

  if (scenario.kind === 'full-balance' || scenario.kind === 'full-voucher') {
    return scenario.kind;
  }

  if (
    payable > 0 &&
    (scenario.kind === 'no-balance' || scenario.kind === 'partial-balance' || scenario.kind === 'partial-voucher' || scenario.kind === 'mixed' || scenario.kind === 'discount')
  ) {
    return scenario.kind;
  }

  return undefined;
}

function hasEmbeddedCashierContinueTracking(variant: EmbeddedCashierVariant | undefined): variant is EmbeddedCashierVariant {
  return variant !== undefined && EMBEDDED_CASHIER_TRACKED_VARIANTS.includes(variant);
}

function isBelowMinimumPreStep(variant: EmbeddedCashierVariant | undefined, payable: number, hasAcknowledgedBelowMinimum: boolean): boolean {
  return hasEmbeddedCashierContinueTracking(variant) && payable > 0 && payable < CHECKOUT_BELOW_MINIMUM_PRE_STEP_THRESHOLD && !hasAcknowledgedBelowMinimum;
}

function buildCheckoutPreStepContinueTracking(): Record<string, string> {
  return {
    'data-track-name': 'Continue',
  };
}

function skipsEmbeddedCashierTermsDisabledGate(variant: EmbeddedCashierVariant | undefined): boolean {
  return variant === 'no-balance' || variant === 'discount';
}

function buildDepositTypeTracking(depositType?: DepositTrackingType): Record<string, string> {
  return depositType ? { 'data-track-type': depositType } : {};
}

function buildEmbeddedCashierAmountContainerTracking(variant: EmbeddedCashierVariant | undefined, depositType?: DepositTrackingType) {
  switch (variant) {
    case 'no-balance':
      return {
        'data-track-category': CHECKOUT_EMBEDDED_CASHIER_TRACKING.category,
        ...buildDepositTypeTracking(depositType),
        'data-track-count': CHECKOUT_EMBEDDED_CASHIER_TRACKING.count,
      };
    case 'discount':
      return {
        'data-track-category': CHECKOUT_DISCOUNT_TRACKING.category,
        ...buildDepositTypeTracking(depositType),
        'data-track-count': CHECKOUT_DISCOUNT_TRACKING.count,
      };
    case 'full-balance':
      return { 'data-track-category': CHECKOUT_FULL_BALANCE_TRACKING.category };
    case 'partial-balance':
      return { 'data-track-category': CHECKOUT_PARTIAL_BALANCE_TRACKING.category };
    case 'partial-voucher':
      return {
        'data-track-category': CHECKOUT_PARTIAL_VOUCHER_TRACKING.category,
        ...buildDepositTypeTracking(depositType),
        'data-track-count': CHECKOUT_PARTIAL_VOUCHER_TRACKING.count,
      };
    case 'mixed':
      return {
        'data-track-category': CHECKOUT_MIXED_TRACKING.category,
        ...buildDepositTypeTracking(depositType),
        'data-track-count': CHECKOUT_MIXED_TRACKING.count,
      };
    case 'full-voucher':
      return { 'data-track-category': CHECKOUT_VOUCHER_TRACKING.category };
    default:
      return {};
  }
}

function buildEmbeddedCashierContinueButtonTracking(
  variant: EmbeddedCashierVariant,
  {
    stakeInMinorUnits,
    selectedMethod,
    depositAmount,
    depositType,
  }: {
    stakeInMinorUnits: number;
    selectedMethod: MethodOrInstrument | null;
    depositAmount: string;
    depositType?: DepositTrackingType;
  }
): Record<string, string> {
  switch (variant) {
    case 'full-balance':
      return buildCheckoutFullBalanceContinueTracking(stakeInMinorUnits);
    case 'full-voucher':
      return buildCheckoutVoucherContinueTracking(stakeInMinorUnits);
    default:
      return buildCheckoutContinueTracking(selectedMethod, depositAmount, depositType);
  }
}

function createEmbeddedCashierContinueHandler(variant: EmbeddedCashierVariant, context: EmbeddedCashierContinueContext) {
  switch (variant) {
    case 'full-balance':
    case 'full-voucher':
      return () => context.onPay(null);
    case 'no-balance':
    case 'discount':
      return context.onNoBalanceContinue;
    case 'partial-balance':
    case 'partial-voucher':
    case 'mixed':
      return () => context.onPay(context.selectedMethod);
  }
}

function buildCheckoutContinueTracking(method: MethodOrInstrument | null, amount: string, depositType?: DepositTrackingType): Record<string, string> {
  return {
    'data-track-name': 'Continue',
    'data-track-method': method?.['data-track-method'] ?? method?.name ?? '',
    'data-track-provider': method?.['data-track-provider'] ?? method?.apiName ?? '',
    ...buildDepositTypeTracking(depositType),
    'data-track-count': CHECKOUT_EMBEDDED_CASHIER_TRACKING.count,
    'data-track-amount': amount,
  };
}

function buildCheckoutFullBalanceContinueTracking(stakeInMinorUnits: number): Record<string, string> {
  return {
    'data-track-name': 'Continue',
    'data-track-method': CHECKOUT_FULL_BALANCE_TRACKING.method,
    'data-track-amount': formatMinorUnits(stakeInMinorUnits),
  };
}

function buildCheckoutVoucherContinueTracking(stakeInMinorUnits: number): Record<string, string> {
  return {
    'data-track-name': 'Continue',
    'data-track-method': CHECKOUT_VOUCHER_TRACKING.method,
    'data-track-amount': formatMinorUnits(stakeInMinorUnits),
  };
}

export {
  buildCheckoutContinueTracking,
  buildCheckoutFullBalanceContinueTracking,
  buildCheckoutPaymentFailureTracking,
  buildCheckoutPreStepContinueTracking,
  buildCheckoutVoucherContinueTracking,
  buildEmbeddedCashierAmountContainerTracking,
  buildEmbeddedCashierContinueButtonTracking,
  CHECKOUT_BELOW_MINIMUM_PRE_STEP_THRESHOLD,
  CHECKOUT_DIALOG_TRACKING,
  CHECKOUT_DISCOUNT_TRACKING,
  CHECKOUT_EMBEDDED_CASHIER_TRACKING,
  CHECKOUT_FULL_BALANCE_TRACKING,
  CHECKOUT_MIXED_TRACKING,
  CHECKOUT_PARTIAL_BALANCE_TRACKING,
  CHECKOUT_PARTIAL_VOUCHER_TRACKING,
  CHECKOUT_VOUCHER_TRACKING,
  createEmbeddedCashierContinueHandler,
  getDepositTrackingType,
  getEmbeddedCashierVariant,
  hasEmbeddedCashierContinueTracking,
  isBelowMinimumPreStep,
  mapCheckoutCartSubmitErrorContainerTracking,
  mapCheckoutCartSubmitErrorTracking,
  mapCheckoutPaymentErrorTracking,
  skipsEmbeddedCashierTermsDisabledGate,
  type CheckoutDialogTracking,
  type CheckoutPaymentErrorContext,
  type DepositTrackingType,
  type EmbeddedCashierVariant,
};
